import { createEntityAdapter } from "@reduxjs/toolkit";

import apiSlice from "../api/apiSlice";
import { lisbonToday } from "../../utils/businessValidity";

const adminAdapter = createEntityAdapter({});

const initialState = adminAdapter.getInitialState({});

export const adminApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* GET */
    getEMSModules: builder.query({
      query: () => ({
        url: `/permissions/ems-modules`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
    }),
    getAllCommunities: builder.query({
      query: () => ({
        url: `/auth/admin/communities`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: ["Communities"],
    }),
    getAllUsers: builder.query({
      query: () => ({
        url: `/auth/users`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: ["Users"],
      transformResponse: (responseData) => {
        if (responseData) {
          return adminAdapter.setAll(initialState, responseData);
        }
        return initialState;
      },
    }),
    getAllPermissions: builder.query({
      query: () => ({
        url: `/permissions`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: ["Permissions"],
      transformResponse: (responseData) => {
        if (responseData) {
          return adminAdapter.setAll(initialState, responseData);
        }
        return initialState;
      },
    }),
    /* CREATE super user */
    createSuperUser: builder.mutation({
      query: (user) => ({
        url: `/auth/signup`,
        method: "POST",
        body: { ...user, role: "SUPER_USER" },
      }),
      invalidatesTags: ["Users"],
    }),
    /* CREATE community admin */
    createCommunityAdmin: builder.mutation({
      query: ({ communityId, ...member }) => ({
        url: `/communities/${communityId}/users`,
        method: "POST",
        body: { ...member, role: "ADMIN" },
      }),
      invalidatesTags: ["Users"],
    }),
    /* UPDATE */
    updatePermissionsById: builder.mutation({
      query: ({ data }) => ({
        url: `/permissions`,
        method: "PUT",
        body: { ...data },
      }),
      async onQueryStarted({ data }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          adminApiSlice.util.updateQueryData("getAllPermissions", {}, (draft) => {
            const permission = draft.entities[data.id];
            if (permission) {
              permission.is_active = data.is_active;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: ["Users", "MyPermissions"],
    }),
    updateUserRole: builder.mutation({
      query: ({ userId, role }) => ({
        url: `/auth/users/${userId}/role`,
        method: "PATCH",
        body: { role },
      }),
      invalidatesTags: ["Users", "Permissions"],
    }),
    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `/auth/users/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Users", "Permissions"],
    }),
    linkUserToCommunity: builder.mutation({
      query: ({ userId, communityId, role, startDate, endDate }) => ({
        url:
          `/auth/users/${userId}/communities/${communityId}?` +
          `role=${encodeURIComponent(role || "MEMBER")}&` +
          `start_date=${encodeURIComponent(startDate || lisbonToday())}` +
          `${endDate ? `&end_date=${encodeURIComponent(endDate)}` : ""}`,
        method: "POST",
      }),
      invalidatesTags: ["Users"],
    }),
    unlinkUserFromCommunity: builder.mutation({
      query: ({ userId, communityId, effectiveAt }) => ({
        url:
          `/auth/users/${userId}/communities/${communityId}?` +
          `effective_at=${encodeURIComponent(effectiveAt || lisbonToday())}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Users"],
    }),
    updateUserById: builder.mutation({
      query: ({ userId, data }) => ({
        url: `/auth/users/${userId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Users"],
    }),
  }),
});

export const {
  useGetEMSModulesQuery,
  useGetAllCommunitiesQuery,
  useGetAllUsersQuery,
  useGetAllPermissionsQuery,
  useUpdatePermissionsByIdMutation,
  useCreateSuperUserMutation,
  useCreateCommunityAdminMutation,
  useUpdateUserRoleMutation,
  useDeleteUserMutation,
  useLinkUserToCommunityMutation,
  useUnlinkUserFromCommunityMutation,
  useUpdateUserByIdMutation,
} = adminApiSlice;

export const { selectAll: selectAllUsers, selectAll: selectAllPermissions } =
  adminAdapter.getSelectors();
